export const SPATIAL_VISION_COLOR_PALETTE = [
  '#DC2626',
  '#059669',
  '#7C3AED',
  '#EA580C',
  '#0891B2',
  '#BE123C',
  '#4F46E5',
  '#16A34A',
];

export function normalizeOverlayColor(value) {
  const color = String(value || '').trim();
  return /^#[\dA-F]{6}$/i.test(color) ? color.toUpperCase() : null;
}

export function stableOverlayColor(value) {
  const text = String(value ?? '');
  let hash = 0;
  for (let index = 0; index < text.length; index += 1) {
    hash = (hash * 31 + text.charCodeAt(index)) >>> 0;
  }
  return SPATIAL_VISION_COLOR_PALETTE[
    hash % SPATIAL_VISION_COLOR_PALETTE.length
  ];
}

export function detectionOverlayKey(detection) {
  const box = detection?.bbox || {};
  return String(
    detection?.id ||
      detection?.detectionId ||
      detection?.resultId ||
      [
        detection?.stepId ?? '',
        detection?.stepIndex ?? '',
        detection?.label || detection?.title || '',
        box.x ?? detection?.coordX ?? 0,
        box.y ?? detection?.coordY ?? 0,
        box.w ?? detection?.width ?? 0,
        box.h ?? detection?.height ?? 0,
      ].join('|'),
  );
}

const conditionResultIndexes = result => {
  const indexes =
    result?.relatedDetectionIndexes || result?.related_detection_indexes || [];
  return Array.isArray(indexes) ? indexes : [];
};

const isMatchedResult = result =>
  String(result?.resultStatus || result?.result_status || '')
    .trim()
    .toLowerCase()
    .replace(/[-\s]+/g, '_') === 'matched';

export function conditionResultColor(result, resultIndex = 0) {
  const explicitColor = normalizeOverlayColor(
    result?.statusLabelColor || result?.status_label_color,
  );
  if (explicitColor) return explicitColor;

  if (!(result?.statusLabel || result?.status_label)) return null;
  return stableOverlayColor(
    result?.conditionId ||
      result?.condition_id ||
      result?.id ||
      result?.conditionName ||
      result?.condition_name ||
      resultIndex,
  );
}

export function effectiveDetectionColor(
  detection,
  detectionIndex,
  conditionResults = [],
  detectionCount = 0,
) {
  const candidates = (Array.isArray(conditionResults) ? conditionResults : [])
    .map((result, resultIndex) => {
      const indexes = conditionResultIndexes(result);
      return {
        color: conditionResultColor(result, resultIndex),
        matched: isMatchedResult(result),
        linked: indexes.some(index => Number(index) === Number(detectionIndex)),
        // A condition that claims every detection on the frame says nothing about any
        // one of them, and painting all of them in its colour reads as proof it does
        // not have. Leave those boxes their own colour.
        everything: detectionCount > 1 && indexes.length >= detectionCount,
      };
    })
    .filter(candidate => candidate.linked && candidate.color && !candidate.everything);

  const conditionColor =
    candidates.find(candidate => candidate.matched)?.color ||
    candidates[0]?.color;
  if (conditionColor) return conditionColor;

  return (
    normalizeOverlayColor(detection?.color || detection?.colorHex) ||
    stableOverlayColor(detectionOverlayKey(detection) || detectionIndex)
  );
}

export function hiddenDetectionKeySet(hiddenByItem, itemId) {
  if (itemId === null || itemId === undefined) return new Set();
  return new Set(hiddenByItem?.[String(itemId)] || []);
}

export function toggleHiddenDetectionKey(hiddenByItem, itemId, overlayKey) {
  if (itemId === null || itemId === undefined || !overlayKey) {
    return { ...(hiddenByItem || {}) };
  }

  const itemKey = String(itemId);
  const next = { ...(hiddenByItem || {}) };
  const hidden = hiddenDetectionKeySet(next, itemKey);
  const key = String(overlayKey);
  if (hidden.has(key)) hidden.delete(key);
  else hidden.add(key);

  if (hidden.size) next[itemKey] = [...hidden];
  else delete next[itemKey];
  return next;
}

export function setHiddenDetectionKeys(
  hiddenByItem,
  itemId,
  overlayKeys,
  hidden,
) {
  if (itemId === null || itemId === undefined)
    return { ...(hiddenByItem || {}) };
  const itemKey = String(itemId);
  const next = { ...(hiddenByItem || {}) };
  const current = hiddenDetectionKeySet(next, itemKey);
  (overlayKeys || []).forEach(key => {
    if (hidden) current.add(String(key));
    else current.delete(String(key));
  });
  if (current.size) next[itemKey] = [...current];
  else delete next[itemKey];
  return next;
}

export function clearHiddenDetectionKeysForItem(hiddenByItem, itemId) {
  const next = { ...(hiddenByItem || {}) };
  if (itemId !== null && itemId !== undefined) delete next[String(itemId)];
  return next;
}
